// src/pages/EventDetails.js
//
// Détail d'un événement.
//  • Liste des participants (sous-collection 'participants') : prénom, nom,
//    email, source (App / Site), bouton "Actualiser".
//
// NOUVEAU (billetterie workshops / stages) — pour les événements publics :
//  • Résumé des ventes : places vendues, en attente de paiement, montant encaissé.
//  • Jauge par ticket (vendues / réservées / capacité).
//  • Liste des commandes (sous-collection 'orders', écrite par les Cloud
//    Functions) avec filtre par statut, détail (participants, âge, droit à
//    l'image, message de l'acheteur) et note interne.
//  • Actions : « Confirmer le paiement » (virement / espèces après contact à
//    info@) → adminConfirmEventOrder, « Annuler » (commande non payée, libère
//    les places) → adminCancelEventOrder.
//  • Export CSV des participants payés (liste de présence).
//
// NOUVEAU (plusieurs cours) :
//  • Affiche les cours visés (`selectedCourses`, repli sur l'ancien
//    `selectedCourse`) sous forme de chips, au lieu de « Public spécifique ».

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import {
  Box, Paper, Typography, Chip, Divider, Button, List, ListItem,
  ListItemText, ListItemAvatar, Avatar, CircularProgress, IconButton,
  Tooltip, Grid, LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, Snackbar, Link, Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  PhoneAndroid as AppIcon,
  Language as WebIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as ViewIcon,
  CheckCircle as ConfirmIcon,
  Cancel as CancelIcon,
  FileDownload as DownloadIcon,
  Instagram as InstagramIcon,
  Place as PlaceIcon,
  Videocam as ImageYesIcon,
  VideocamOff as ImageNoIcon,
  MarkEmailRead as ReminderIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import 'moment/locale/fr';
import {
  ORDER_STATUS, PUBLIC_STATUS, money, readDate, stockOf, ticketSalesSummary,
  minTicketPrice, publicEventUrl, ageAt,
} from '../utils/publicEvents';

moment.locale('fr');

const PARTICIPANTS_SUBCOLLECTION = 'participants';
const ORDERS_SUBCOLLECTION = 'orders';

// Sources venant de l'app mobile
const APP_SOURCES = new Set(['mobile_app', 'agenda_auto', 'mobile_registration']);

const ORDER_TABS = [
  { value: 'active',    label: 'Payées + en attente', match: (s) => ['paid', 'reserved', 'awaiting_payment'].includes(s) },
  { value: 'paid',      label: 'Payées',              match: (s) => s === 'paid' },
  { value: 'pending',   label: 'En attente',          match: (s) => ['reserved', 'awaiting_payment'].includes(s) },
  { value: 'closed',    label: 'Abandonnées / annulées', match: (s) => ['abandoned', 'cancelled'].includes(s) },
  { value: 'all',       label: 'Toutes',              match: () => true },
];

const MANUAL_METHODS = [
  { value: 'bank',  label: 'Virement bancaire' },
  { value: 'cash',  label: 'Espèces' },
  { value: 'other', label: 'Autre' },
];

const METHOD_LABELS = {
  bancontact: 'Bancontact',
  creditcard: 'Carte bancaire',
  free: 'Gratuit',
  manual_bank: 'Virement (manuel)',
  manual_cash: 'Espèces (manuel)',
  manual_other: 'Autre (manuel)',
};

const CALLABLE_ERRORS = {
  ORDER_PAID: 'Cette commande est déjà payée : elle ne peut pas être annulée ici.',
  ORDER_NOT_FOUND: 'Commande introuvable (supprimée ?).',
  STAFF_ONLY: "Votre compte n'a pas les droits admin (collection admins).",
  AUTH_REQUIRED: 'Session expirée, reconnectez-vous.',
};

const callableError = (e) => {
  const code = e?.message || '';
  return CALLABLE_ERRORS[code] || `Erreur : ${code || 'inconnue'}`;
};

const formatDateTime = (value) => {
  const d = readDate(value);
  return d ? moment(d).format('DD/MM/YYYY à HH:mm') : '—';
};

const OrderStatusChip = ({ status }) => {
  const s = ORDER_STATUS[status] || { label: status || '—', bg: '#f5f5f5', color: '#616161' };
  return <Chip size="small" label={s.label} sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }} />;
};

const ConsentChip = ({ value }) => (
  value
    ? <Chip size="small" icon={<ImageYesIcon fontSize="small" />} label="Oui" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
    : <Chip size="small" icon={<ImageNoIcon fontSize="small" />} label="Non" sx={{ backgroundColor: '#ffebee', color: '#c62828' }} />
);

function StatTile({ label, value, sub, color = '#2c3e50' }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ color, mt: 0.5 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

export default function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [eventData,    setEventData]    = useState(null);
  const [participants, setParticipants] = useState([]);
  const [orders,       setOrders]       = useState([]);
  const [courseTitles, setCourseTitles] = useState({});
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [orderTab,     setOrderTab]     = useState('active');

  const [detailOrder,  setDetailOrder]  = useState(null);
  const [adminNote,    setAdminNote]    = useState('');
  const [savingNote,   setSavingNote]   = useState(false);

  const [confirmOrder, setConfirmOrder] = useState(null);
  const [confirmMethod, setConfirmMethod] = useState('bank');
  const [confirmNote,  setConfirmNote]  = useState('');
  const [actionBusy,   setActionBusy]   = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const functions = useMemo(() => getFunctions(getApp(), 'europe-west1'), []);

  const loadData = useCallback(async (showFullLoader = true) => {
    if (showFullLoader) setLoading(true);
    else                setRefreshing(true);
    try {
      const snap = await getDoc(doc(db, 'events', id));
      if (!snap.exists()) { setEventData(null); return; }
      const data = snap.data();

      const partsSnap = await getDocs(collection(db, 'events', id, PARTICIPANTS_SUBCOLLECTION));
      const parts     = partsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      let ords = [];
      try {
        const ordersSnap = await getDocs(collection(db, 'events', id, ORDERS_SUBCOLLECTION));
        ords = ordersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (readDate(b.createdAt)?.getTime() || 0) - (readDate(a.createdAt)?.getTime() || 0));
      } catch (e) {
        // Rules pas encore déployées ou compte non staff : on affiche le reste.
        console.warn('Lecture des commandes impossible :', e);
      }

      // Titres des cours visés (pour l'affichage des chips)
      const targetIds = [
        ...(Array.isArray(data.selectedCourses) ? data.selectedCourses : []),
        data.selectedCourse,
      ].filter(Boolean);
      if (targetIds.length > 0) {
        const titles = {};
        await Promise.all([...new Set(targetIds)].map(async (courseId) => {
          try {
            const c = await getDoc(doc(db, 'dance_courses', courseId));
            titles[courseId] = c.exists() ? (c.data().title || courseId) : 'Cours supprimé';
          } catch {
            titles[courseId] = courseId;
          }
        }));
        setCourseTitles(titles);
      }

      setEventData({
        id,
        ...data,
        startDate: data.startDate?.toDate(),
        endDate:   data.endDate?.toDate(),
      });
      setParticipants(parts);
      setOrders(ords);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Actions sur les commandes ──────────────────────────────────────────

  const openDetail = (order) => {
    setDetailOrder(order);
    setAdminNote(order.adminNote || '');
  };

  const saveAdminNote = async () => {
    if (!detailOrder) return;
    setSavingNote(true);
    try {
      await updateDoc(doc(db, 'events', id, ORDERS_SUBCOLLECTION, detailOrder.id), {
        adminNote: adminNote.trim(),
        updatedAt: serverTimestamp(),
      });
      setOrders(prev => prev.map(o => (o.id === detailOrder.id ? { ...o, adminNote: adminNote.trim() } : o)));
      setSnackbar({ open: true, message: 'Note enregistrée', severity: 'success' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: "La note n'a pas pu être enregistrée", severity: 'error' });
    } finally {
      setSavingNote(false);
    }
  };

  const openConfirm = (order) => {
    setConfirmOrder(order);
    setConfirmMethod('bank');
    setConfirmNote('');
  };

  const doConfirm = async () => {
    if (!confirmOrder) return;
    setActionBusy(true);
    try {
      const res = await httpsCallable(functions, 'adminConfirmEventOrder')({
        eventId: id,
        orderId: confirmOrder.id,
        method: confirmMethod,
        note: confirmNote,
      });
      setConfirmOrder(null);
      setDetailOrder(null);
      setSnackbar({
        open: true,
        severity: res.data?.oversold ? 'warning' : 'success',
        message: res.data?.already
          ? 'Cette commande était déjà payée.'
          : res.data?.oversold
            ? 'Paiement confirmé — attention, la jauge est maintenant dépassée.'
            : 'Paiement confirmé, mail de confirmation envoyé.',
      });
      await loadData(false);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: callableError(e), severity: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const doCancel = async (order) => {
    const who = `${order.buyer?.firstName || ''} ${order.buyer?.lastName || ''}`.trim();
    if (!window.confirm(`Annuler la commande de ${who} (${order.participants?.length || 0} place(s)) ?\n\nLes places seront remises en vente. Aucun mail n'est envoyé.`)) return;
    setActionBusy(true);
    try {
      await httpsCallable(functions, 'adminCancelEventOrder')({ eventId: id, orderId: order.id });
      setDetailOrder(null);
      setSnackbar({ open: true, message: 'Commande annulée, places libérées.', severity: 'success' });
      await loadData(false);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: callableError(e), severity: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  // ── Données dérivées ───────────────────────────────────────────────────

  const isPublic = eventData?.isPublic === true;
  const tickets = useMemo(() => (Array.isArray(eventData?.ticketTypes) ? eventData.ticketTypes : []), [eventData]);
  const sales = useMemo(() => ticketSalesSummary(eventData || {}), [eventData]);

  const orderStats = useMemo(() => {
    const paid = orders.filter(o => o.status === 'paid');
    const pending = orders.filter(o => ['reserved', 'awaiting_payment'].includes(o.status));
    return {
      revenue: paid.reduce((s, o) => s + Number(o.totalAmount || 0), 0),
      paidCount: paid.length,
      pendingCount: pending.length,
      pendingSeats: pending.reduce((s, o) => s + (o.participants?.length || 0), 0),
      pendingAmount: pending.reduce((s, o) => s + Number(o.totalAmount || 0), 0),
      abandoned: orders.filter(o => o.status === 'abandoned').length,
      issues: orders.filter(o => o.oversold || o.paymentIssue).length,
    };
  }, [orders]);

  const tabMatch = ORDER_TABS.find(t => t.value === orderTab)?.match || (() => true);
  const visibleOrders = orders.filter(o => tabMatch(o.status));

  const csvData = useMemo(() => {
    const rows = [];
    orders.filter(o => o.status === 'paid').forEach((o) => {
      (o.participants || []).forEach((p) => {
        rows.push({
          'Participant': `${p.firstName || ''} ${p.lastName || ''}`.trim(),
          'Date de naissance': p.dateOfBirth || '',
          'Âge': ageAt(p.dateOfBirth, eventData?.startDate) ?? '',
          'Ticket': p.ticketName || '',
          'Prix': p.unitPrice ?? '',
          "Droit à l'image": p.imageConsent ? 'Oui' : 'Non',
          'Acheteur': `${o.buyer?.firstName || ''} ${o.buyer?.lastName || ''}`.trim(),
          'Email': o.buyer?.email || '',
          'Téléphone': o.buyer?.phone || '',
          'Paiement': METHOD_LABELS[o.paymentMethod] || o.paymentMethod || '',
          'Commande': o.id,
          'Date commande': formatDateTime(o.createdAt),
          'Message acheteur': o.note || '',
          'Note interne': o.adminNote || '',
        });
      });
    });
    return rows.sort((a, b) => a.Participant.localeCompare(b.Participant, 'fr'));
  }, [orders, eventData]);

  // ── Rendu ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!eventData) {
    return (
      <Box p={3}>
        <Typography>Événement introuvable.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>Retour</Button>
      </Box>
    );
  }

  const appCount  = participants.filter(p => APP_SOURCES.has(p.source)).length;
  const siteCount = participants.length - appCount;
  const pageUrl = publicEventUrl(eventData);
  const statusMeta = PUBLIC_STATUS[eventData.status] || PUBLIC_STATUS.draft;
  const fromPrice = minTicketPrice(eventData);
  const targetCourseIds = (Array.isArray(eventData.selectedCourses) && eventData.selectedCourses.length > 0)
    ? eventData.selectedCourses
    : (eventData.selectedCourse ? [eventData.selectedCourse] : []);

  return (
    <Box p={3} sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')}>
          Retour
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {isPublic && pageUrl && eventData.status === 'published' && (
          <Button variant="text" endIcon={<OpenInNewIcon />} href={pageUrl} target="_blank" rel="noopener">
            Voir la page
          </Button>
        )}
        <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/events/${id}/edit`)}>
          Modifier
        </Button>
        <Tooltip title="Actualiser">
          <IconButton onClick={() => loadData(false)} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Infos événement ──────────────────────────────────────────────── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {eventData.imageUrl && (
            <Grid item xs={12} md={4}>
              <Box
                component="img"
                src={eventData.imageUrl}
                alt=""
                sx={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 2 }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={eventData.imageUrl ? 8 : 12}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {eventData.title}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              {isPublic && <Chip label={`Site : ${statusMeta.label}`} color={statusMeta.color} />}
              {eventData.category && <Chip label={eventData.category} variant="outlined" />}
              {isPublic ? (
                <Chip
                  label={fromPrice === null ? 'Aucun ticket' : fromPrice > 0 ? `Dès ${money(fromPrice)}` : 'Gratuit'}
                  color={fromPrice > 0 ? 'primary' : 'success'}
                />
              ) : (
                <Chip
                  label={eventData.price > 0 ? `${eventData.price} €` : 'Gratuit'}
                  color={eventData.price > 0 ? 'primary' : 'success'}
                />
              )}
              {eventData.isForAllStudents !== false
                ? (!isPublic && <Chip label="Tous les élèves" color="success" />)
                : targetCourseIds.length > 0
                  ? targetCourseIds.map((courseId) => (
                      <Chip
                        key={courseId}
                        label={courseTitles[courseId] || '…'}
                        color="warning"
                        variant="outlined"
                        onClick={() => navigate(`/courses/${courseId}`)}
                      />
                    ))
                  : <Chip label="Public spécifique" color="warning" />}
              {isPublic && eventData.level && <Chip label={eventData.level} variant="outlined" />}
              {isPublic && (eventData.ageMin != null || eventData.ageMax != null) && (
                <Chip
                  variant="outlined"
                  label={
                    eventData.ageMin != null && eventData.ageMax != null
                      ? `${eventData.ageMin}–${eventData.ageMax} ans`
                      : eventData.ageMin != null ? `Dès ${eventData.ageMin} ans` : `Jusqu'à ${eventData.ageMax} ans`
                  }
                />
              )}
            </Box>
            <Typography sx={{ mb: 1 }}>
              <strong>Début :</strong>{' '}
              {eventData.startDate ? moment(eventData.startDate).format('dddd D MMMM YYYY à HH:mm') : '—'}
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <strong>Fin :</strong>{' '}
              {eventData.endDate ? moment(eventData.endDate).format('dddd D MMMM YYYY à HH:mm') : '—'}
            </Typography>
            {isPublic && (
              <Typography sx={{ mb: 1 }}>
                <strong>Inscriptions jusqu'au :</strong>{' '}
                {eventData.registrationDeadline ? formatDateTime(eventData.registrationDeadline) : 'début de l\'événement'}
              </Typography>
            )}
            {isPublic && (eventData.location?.name || eventData.location?.address) && (
              <Typography sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlaceIcon fontSize="small" color="action" />
                {[eventData.location?.name, eventData.location?.address].filter(Boolean).join(' — ')}
              </Typography>
            )}
            {isPublic && eventData.instagramUrl && (
              <Link href={eventData.instagramUrl} target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <InstagramIcon fontSize="small" /> Post Instagram
              </Link>
            )}
          </Grid>
        </Grid>
        {eventData.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ whiteSpace: 'pre-line' }}>{eventData.description}</Typography>
          </>
        )}
      </Paper>

      {/* ── Billetterie ──────────────────────────────────────────────────── */}
      {isPublic && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Billetterie</Typography>

          {orderStats.issues > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              {orderStats.issues} commande(s) à vérifier (jauge dépassée ou montant incorrect) — voir la liste ci-dessous.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Places vendues"
                value={sales.capacity === null ? sales.sold : `${sales.sold} / ${sales.capacity}`}
                sub={sales.capacity === null ? 'capacité illimitée' : `${Math.max(sales.capacity - sales.sold - sales.reserved, 0)} disponible(s)`}
                color="#7C3AED"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Encaissé"
                value={money(orderStats.revenue)}
                sub={`${orderStats.paidCount} commande(s) payée(s)`}
                color="#2e7d32"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="En attente de paiement"
                value={orderStats.pendingSeats}
                sub={orderStats.pendingCount ? `${orderStats.pendingCount} commande(s) · ${money(orderStats.pendingAmount)}` : 'place(s) bloquée(s)'}
                color="#e65100"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile
                label="Abandonnées"
                value={orderStats.abandoned}
                sub="relancées sans paiement"
                color="#455a64"
              />
            </Grid>
          </Grid>

          <Stack spacing={1.5}>
            {tickets.map((t) => {
              const { sold, reserved } = stockOf(eventData.ticketStock, t.id);
              const cap = t.quantity === null || t.quantity === undefined ? null : Number(t.quantity);
              const soldPct = cap ? Math.min((sold / cap) * 100, 100) : 0;
              const bufferPct = cap ? Math.min(((sold + reserved) / cap) * 100, 100) : 0;
              return (
                <Box key={t.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t.name_i18n?.fr || t.name || t.id} — {Number(t.price) > 0 ? money(t.price) : 'Gratuit'}
                      {t.active === false && <Chip size="small" label="Masqué" sx={{ ml: 1, height: 20 }} />}
                      {cap !== null && sold + reserved >= cap && <Chip size="small" color="error" label="Complet" sx={{ ml: 1, height: 20 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sold} vendue(s){reserved ? ` · ${reserved} en attente` : ''}{cap !== null ? ` / ${cap}` : ' · illimité'}
                    </Typography>
                  </Box>
                  {cap !== null && cap > 0 && (
                    <LinearProgress
                      variant="buffer"
                      value={soldPct}
                      valueBuffer={bufferPct}
                      sx={{
                        mt: 0.5, height: 8, borderRadius: 4,
                        '& .MuiLinearProgress-bar1Buffer': { backgroundColor: '#7C3AED' },
                        '& .MuiLinearProgress-bar2Buffer': { backgroundColor: '#fdba74' },
                        '& .MuiLinearProgress-dashed': { display: 'none' },
                        backgroundColor: '#eee',
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* ── Commandes ────────────────────────────────────────────────────── */}
      {(isPublic || orders.length > 0) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">Commandes ({orders.length})</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <CSVLink
              data={csvData}
              filename={`participants-${(eventData.slug || id)}-${moment().format('YYYYMMDD')}.csv`}
              style={{ textDecoration: 'none', pointerEvents: csvData.length ? 'auto' : 'none' }}
            >
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={!csvData.length}>
                Liste des participants (CSV)
              </Button>
            </CSVLink>
          </Box>

          <Tabs
            value={orderTab}
            onChange={(_, v) => setOrderTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ mb: 1 }}
          >
            {ORDER_TABS.map(t => (
              <Tab key={t.value} value={t.value} label={`${t.label} (${orders.filter(o => t.match(o.status)).length})`} />
            ))}
          </Tabs>

          {visibleOrders.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              Aucune commande dans cette catégorie.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Acheteur</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Participant(s)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Montant</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleOrders.map((o) => {
                    const canConfirm = ['reserved', 'awaiting_payment', 'abandoned'].includes(o.status);
                    const canCancel = ['reserved', 'awaiting_payment', 'abandoned'].includes(o.status);
                    return (
                      <TableRow key={o.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(o.createdAt)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {`${o.buyer?.firstName || ''} ${o.buyer?.lastName || ''}`.trim() || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{o.buyer?.email}</Typography>
                          {o.buyer?.phone && (
                            <Typography variant="caption" color="text.secondary" display="block">{o.buyer.phone}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {(o.participants || []).map((p, i) => (
                            <Typography key={i} variant="body2">
                              {p.firstName} {p.lastName}
                              <Typography component="span" variant="caption" color="text.secondary"> · {p.ticketName}</Typography>
                            </Typography>
                          ))}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {o.totalAmount > 0 ? money(o.totalAmount) : 'Gratuit'}
                          {o.paymentMethod && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {METHOD_LABELS[o.paymentMethod] || o.paymentMethod}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                            <OrderStatusChip status={o.status} />
                            {o.reminderSentAt && o.status !== 'paid' && (
                              <Tooltip title={`Relance envoyée le ${formatDateTime(o.reminderSentAt)}`}>
                                <Chip size="small" variant="outlined" icon={<ReminderIcon fontSize="small" />} label="Relancée" />
                              </Tooltip>
                            )}
                            {['reserved', 'awaiting_payment'].includes(o.status) && o.holdUntil && (
                              <Typography variant="caption" color="text.secondary">
                                Place gardée jusqu'au {formatDateTime(o.holdUntil)}
                              </Typography>
                            )}
                            {o.oversold && <Chip size="small" color="error" label="Jauge dépassée" />}
                            {o.paymentIssue === 'amount_mismatch' && <Chip size="small" color="error" label="Montant à vérifier" />}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Détail">
                            <IconButton size="small" onClick={() => openDetail(o)}><ViewIcon /></IconButton>
                          </Tooltip>
                          {canConfirm && (
                            <Tooltip title="Confirmer le paiement (virement, espèces…)">
                              <IconButton size="small" color="success" onClick={() => openConfirm(o)} disabled={actionBusy}>
                                <ConfirmIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canCancel && (
                            <Tooltip title="Annuler la commande (libère les places)">
                              <IconButton size="small" color="error" onClick={() => doCancel(o)} disabled={actionBusy}>
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* ── Participants ─────────────────────────────────────────────────── */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight="bold">
            Participants ({participants.length})
          </Typography>
          {participants.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {appCount > 0  && <Chip icon={<AppIcon />} label={`${appCount} via App`}  size="small" sx={{ borderColor: '#7C3AED', color: '#7C3AED' }} variant="outlined" />}
              {siteCount > 0 && <Chip icon={<WebIcon />} label={`${siteCount} via Site`} size="small" variant="outlined" />}
            </Box>
          )}
        </Box>

        {participants.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 48, color: '#ccc', display: 'block', mx: 'auto', mb: 1 }} />
            <Typography color="text.secondary">Aucun inscrit pour le moment.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {participants.map((p, idx) => {
              const isApp   = APP_SOURCES.has(p.source);
              const name    = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.pseudo || '—';
              const initial = name !== '—' ? name[0].toUpperCase() : '?';

              return (
                <ListItem
                  key={p.id}
                  divider={idx < participants.length - 1}
                  sx={{ px: 0, py: 1.5 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ backgroundColor: isApp ? '#EDE9FE' : '#f0f0f0', color: isApp ? '#7C3AED' : '#555' }}>
                      {initial}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography fontWeight="bold">{name}</Typography>
                        {isApp
                          ? <Chip icon={<AppIcon />} label="App" size="small" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#EDE9FE', color: '#7C3AED' }} />
                          : <Chip icon={<WebIcon />} label="Site" size="small" sx={{ height: 20, fontSize: '0.7rem' }} variant="outlined" />}
                        {p.ticketName && <Chip label={p.ticketName} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
                        {p.imageConsent === false && (
                          <Tooltip title="Pas de droit à l'image">
                            <ImageNoIcon fontSize="small" sx={{ color: '#c62828' }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                        {p.email && (
                          <Typography component="span" variant="caption" color="text.secondary">{p.email}</Typography>
                        )}
                        {p.joinedAt?.toDate && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            Inscrit le {moment(p.joinedAt.toDate()).format('DD/MM/YYYY à HH:mm')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      {/* ── Dialog : détail d'une commande ───────────────────────────────── */}
      <Dialog open={!!detailOrder} onClose={() => setDetailOrder(null)} maxWidth="md" fullWidth>
        {detailOrder && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Commande du {formatDateTime(detailOrder.createdAt)}
              <Box sx={{ flexGrow: 1 }} />
              <OrderStatusChip status={detailOrder.status} />
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Acheteur</Typography>
                  <Typography>{detailOrder.buyer?.firstName} {detailOrder.buyer?.lastName}</Typography>
                  <Typography>
                    <Link href={`mailto:${detailOrder.buyer?.email}`}>{detailOrder.buyer?.email}</Link>
                  </Typography>
                  {detailOrder.buyer?.phone && <Typography>{detailOrder.buyer.phone}</Typography>}
                  <Typography variant="caption" color="text.secondary">
                    {detailOrder.registrationType === 'child' ? 'Inscription pour enfant(s)' : 'Inscription adulte'} · langue {String(detailOrder.locale || 'fr').toUpperCase()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Paiement</Typography>
                  {(detailOrder.items || []).map((it) => (
                    <Typography key={it.ticketTypeId} variant="body2">
                      {it.quantity} × {it.name} — {it.subtotal > 0 ? money(it.subtotal) : 'Gratuit'}
                    </Typography>
                  ))}
                  <Typography fontWeight="bold" sx={{ mt: 0.5 }}>
                    Total : {detailOrder.totalAmount > 0 ? money(detailOrder.totalAmount) : 'Gratuit'}
                  </Typography>
                  {detailOrder.paymentMethod && (
                    <Typography variant="body2">Moyen : {METHOD_LABELS[detailOrder.paymentMethod] || detailOrder.paymentMethod}</Typography>
                  )}
                  {detailOrder.paidAt && <Typography variant="body2">Payé le {formatDateTime(detailOrder.paidAt)}</Typography>}
                  {detailOrder.reminderSentAt && (
                    <Typography variant="body2">Relance envoyée le {formatDateTime(detailOrder.reminderSentAt)}</Typography>
                  )}
                  {detailOrder.confirmationNote && (
                    <Typography variant="body2">Note de confirmation : {detailOrder.confirmationNote}</Typography>
                  )}
                  {detailOrder.lastPaymentStatus && detailOrder.status !== 'paid' && (
                    <Typography variant="body2" color="text.secondary">Dernier statut Mollie : {detailOrder.lastPaymentStatus}</Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Participant(s)</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Naissance</TableCell>
                        <TableCell>Âge à l'événement</TableCell>
                        <TableCell>Ticket</TableCell>
                        <TableCell>Droit à l'image</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detailOrder.participants || []).map((p, i) => (
                        <TableRow key={i}>
                          <TableCell>{p.firstName} {p.lastName}</TableCell>
                          <TableCell>{p.dateOfBirth ? moment(p.dateOfBirth).format('DD/MM/YYYY') : '—'}</TableCell>
                          <TableCell>{ageAt(p.dateOfBirth, eventData.startDate) ?? '—'}</TableCell>
                          <TableCell>{p.ticketName} ({Number(p.unitPrice) > 0 ? money(p.unitPrice) : 'gratuit'})</TableCell>
                          <TableCell><ConsentChip value={p.imageConsent} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>

                {detailOrder.note && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Message de l'acheteur</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f8fafc', whiteSpace: 'pre-line' }}>
                      {detailOrder.note}
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Note interne (visible uniquement dans l'admin)"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value.slice(0, 1000))}
                  />
                  <Button size="small" sx={{ mt: 1 }} onClick={saveAdminNote} disabled={savingNote || adminNote === (detailOrder.adminNote || '')}>
                    {savingNote ? 'Enregistrement…' : 'Enregistrer la note'}
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {['reserved', 'awaiting_payment', 'abandoned'].includes(detailOrder.status) && (
                <>
                  <Button color="error" startIcon={<CancelIcon />} onClick={() => doCancel(detailOrder)} disabled={actionBusy} sx={{ mr: 'auto' }}>
                    Annuler la commande
                  </Button>
                  <Button color="success" variant="contained" startIcon={<ConfirmIcon />} onClick={() => openConfirm(detailOrder)} disabled={actionBusy}>
                    Confirmer le paiement
                  </Button>
                </>
              )}
              <Button onClick={() => setDetailOrder(null)}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Dialog : confirmation manuelle du paiement ───────────────────── */}
      <Dialog open={!!confirmOrder} onClose={() => !actionBusy && setConfirmOrder(null)} maxWidth="sm" fullWidth>
        {confirmOrder && (
          <>
            <DialogTitle>Confirmer le paiement</DialogTitle>
            <DialogContent dividers>
              <Typography sx={{ mb: 2 }}>
                {confirmOrder.buyer?.firstName} {confirmOrder.buyer?.lastName} — {confirmOrder.participants?.length || 0} place(s),{' '}
                <strong>{money(confirmOrder.totalAmount)}</strong>
              </Typography>
              {confirmOrder.status === 'abandoned' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Les places de cette commande avaient été libérées. Si l'événement est complet entre-temps, la jauge sera dépassée.
                </Alert>
              )}
              {confirmOrder.paymentIssue === 'amount_mismatch' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Le montant encaissé en ligne ne correspondait pas au total. Vérifiez sur Mollie avant de confirmer.
                </Alert>
              )}
              <TextField
                select fullWidth label="Moyen de paiement" value={confirmMethod}
                onChange={(e) => setConfirmMethod(e.target.value)} sx={{ mb: 2 }}
              >
                {MANUAL_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
              <TextField
                fullWidth multiline minRows={2} label="Note (optionnel)"
                placeholder="Ex : virement reçu le 12/10, communication OK"
                value={confirmNote}
                onChange={(e) => setConfirmNote(e.target.value.slice(0, 500))}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Les participants seront ajoutés à la liste et l'acheteur recevra le mail de confirmation.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOrder(null)} disabled={actionBusy}>Annuler</Button>
              <Button
                variant="contained" color="success" onClick={doConfirm} disabled={actionBusy}
                startIcon={actionBusy ? <CircularProgress size={16} color="inherit" /> : <ConfirmIcon />}
              >
                Confirmer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}